import {Injectable} from '@angular/core';
import {ParamMap, Params} from '@angular/router';

export type SalesSortDirection = 'asc' | 'desc';

export interface SalesFilterState {
  /** Kürzel; leer = alle. Ungeprüft, bis die Sales-View die Werte gegen die geladenen Optionen prüft. */
  submittedBy: string[];
  probability: string[];
  /** BU-Ids, geprüft wie submittedBy. */
  bu: string[];
  search: string;
  sortKey: string;
  sortDir: SalesSortDirection;
}

export const SALES_SORT_KEYS: readonly string[] = [
  'person', 'firm', 'role', 'customer', 'intermediary', 'bu',
  'conditions', 'submittedBy', 'probability', 'extension', 'status', 'date',
];

export const SALES_PROBABILITIES: readonly string[] = ['10', '25', '50', '75', '90', '100'];

export const SALES_FILTER_PARAMS: readonly string[] = ['submittedBy', 'probability', 'bu', 'search', 'sort', 'dir'];

export function defaultSalesFilterState(): SalesFilterState {
  return {submittedBy: [], probability: [], bu: [], search: '', sortKey: 'person', sortDir: 'asc'};
}

/** Nur die Sales-Parameter einer Adresse, unverändert — für den Weg aus der Detailansicht zurück (NP20-1283). */
export function salesParamsOf(params: ParamMap): Params {
  const result: Params = {};
  for (const key of SALES_FILTER_PARAMS) {
    const value = params.get(key);
    if (value != null) result[key] = value;
  }
  return result;
}

/** Kommagetrennte Liste: getrimmt, ohne leere Elemente und Duplikate (NP20-1283). */
function listOf(value: string | null): string[] {
  const items = (value ?? '').split(',').map(item => item.trim()).filter(item => item !== '');
  return [...new Set(items)];
}

function copyOf(state: SalesFilterState): SalesFilterState {
  return {...state, submittedBy: [...state.submittedBy], probability: [...state.probability], bu: [...state.bu]};
}

/** Gemerkte Filterauswahl der Sales-View (NP20-984) — nur im Arbeitsspeicher, also bis zum Ende des Tabs. */
@Injectable({providedIn: 'root'})
export class SalesFilterStateService {
  private state: SalesFilterState = defaultSalesFilterState();

  read(): SalesFilterState {
    return copyOf(this.state);
  }

  write(state: SalesFilterState): void {
    this.state = copyOf(state);
  }

  hasFilterParams(params: ParamMap): boolean {
    return SALES_FILTER_PARAMS.some(key => params.has(key));
  }

  /** Jeder unbekannte Wert fällt still weg; ein Einzelwert ist eine Liste mit einem Element. */
  fromQueryParams(params: ParamMap): SalesFilterState {
    const state = defaultSalesFilterState();
    const probabilities = listOf(params.get('probability'));
    const sortKey = params.get('sort');
    const sortDir = params.get('dir');

    state.submittedBy = listOf(params.get('submittedBy'));
    state.probability = SALES_PROBABILITIES.filter(value => probabilities.includes(value));
    state.bu = listOf(params.get('bu'));
    const search = params.get('search') ?? '';
    state.search = search.trim() ? search : '';
    if (sortKey != null && SALES_SORT_KEYS.includes(sortKey)) state.sortKey = sortKey;
    if (sortDir === 'asc' || sortDir === 'desc') state.sortDir = sortDir;

    return state;
  }

  /** `null` entfernt den Parameter aus der Adresszeile; Defaults werden nie geschrieben. */
  toQueryParams(state: SalesFilterState): Params {
    return {
      submittedBy: state.submittedBy.length ? state.submittedBy.join(',') : null,
      probability: state.probability.length ? state.probability.join(',') : null,
      bu: state.bu.length ? state.bu.join(',') : null,
      search: state.search.trim() ? state.search : null,
      sort: state.sortKey !== 'person' ? state.sortKey : null,
      dir: state.sortDir !== 'asc' ? state.sortDir : null,
    };
  }
}
